// AI Generated Test
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { default as guidelinesData } from './guidelines-data.json';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { ComplianceService } from '@client/app/blocks/service/compliance/compliance.service';
import { AccessibilityGuidelineVersionService } from '@monsido/modules/accessibility/services/accessibility-guideline-version.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { ExporterService } from '@client/app/modules/export/services/exporter.service';
import { HistoryPrepareService } from '@client/app/pages/global/changes-history/dashboard/tables/history-prepare.service';
import { MonUtilsService } from '@client/app/blocks/service/mon-utils/mon-utils.service';
import { ChangesHistoryHelperService } from '@client/app/pages/global/changes-history/services/changes-history-helper/changes-history-helper.service';
import { safeClone } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'changes-history-dashboard-table-accessibility',
    standalone: false,
    template: `
        <div class="row">
            <div class="col-48">
                <table class="table table-hover table-fixed">
                    <thead>
                    <tr>
                        <th class="col-8 col-lg-7">
                            {{'Crawled at' | translate}}
                        </th>
                        <th class="text-right col-4 col-lg-6">
                            <span class="visible-print-block">{{'Pages' | translate}}</span>
                            <span class="hidden-print">{{'Pages crawled' | translate}}</span>
                        </th>
                        <th class="text-right col-8 col-lg-9">
                            {{'Domain compliance' | translate}}
                        </th>
                        <th *ngIf="allowVerticalAverage" class="text-right col-8 col-lg-9">
                            {{'Comparison to average' | translate}}
                            <span class="fas fa-question-circle hidden-print"
                                  triggers="focus hover"
                                  [ngbTooltip]="'The average score for domains within your industry.' | translate">
                            </span>
                        </th>
                        <th class="text-right col-18 col-lg-14">
                            {{'Failing checks' | translate}}
                        </th>
                        <th class="text-right col-4 col-lg-6">
                            {{'Issues' | translate}}
                        </th>
                        <th class="text-right col-5 col-lg-7">
                            <span class="visible-print-block">{{'Issue changes' | translate}}</span>
                            <span class="hidden-print">{{'Issue changes since previous crawl' | translate}}</span>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <ng-container *ngFor="let story of historyOutput">
                        <tr>
                            <td class="text-v-middle">
                                <user-date [twoLines]="true"
                                           [date]="story.post_processing_done_at || story.date">
                                </user-date>
                            </td>
                            <td class="text-v-middle text-right">
                                {{story.page_count}}
                            </td>
                            <td class="text-right text-v-middle">
                                <table-css-compliance
                                        class="donut"
                                        *ngIf="story.hasCompliance"
                                        [totalCount]="totalCount"
                                        [errorCount]="story.error">
                                </table-css-compliance>
                                <span *ngIf="!story.hasCompliance" translate>
                                    N/A
                                </span>
                            </td>
                            <td *ngIf="allowVerticalAverage" class="text-right text-v-middle comparisonToAverage">
                                <span *ngIf="story.comparisonToAverageStyle === 'positive'" class="positive">
                                    <span class="fas fa-chevron-up"></span> {{story.comparisonToAverage}}
                                </span>
                                <span *ngIf="story.comparisonToAverageStyle === 'negative'" class="negative">
                                    <span class="fas fa-chevron-down"></span> {{story.comparisonToAverage}}
                                </span>
                                <span *ngIf="story.comparisonToAverageStyle === 'neutral'">
                                    {{story.comparisonToAverage}}
                                </span>
                                <span *ngIf="!story.hasCompliance" translate>
                                    N/A
                                </span>
                            </td>
                            <td class="text-v-middle text-right">
                                <ng-container *ngFor="let opportunity of guidelines; trackBy: trackByIndex">
                                    <span class="outer-r-mini" *ngIf="opportunity.guidelineId | accessibilityHasGuidelineId:domain">
                                        <span [ngClass]="opportunity.icon">
                                            {{opportunity.attr}}
                                        </span>
                                        <span>{{story?.accessibility_checks?.[opportunity.attr] || 0 | monNumber }}</span>
                                    </span>
                                </ng-container>
                            </td>
                            <td class="text-right text-v-middle">
                                {{story.accessiblity_issues_count }}
                            </td>
                            <td class="text-v-middle text-right"
                                [ngClass]="story.changeValueColorClass">
                                <span [ngClass]="story.changeValueIconClass">
                                </span>
                                {{story.changePercentageValue}}
                            </td>
                        </tr>
                        <tr *ngIf="story.versionChange">
                          <td [attr.colspan]="allowVerticalAverage ? 7 : 6">
                            <div class="accessibility-update-indicator">
                                <a class="accessibility-update-indicator-link" [attr.href]="story.versionChange.infoLink" target="_blank">{{ story.versionChange.title }}</a>
                            </div>
                          </td>
                        </tr>
                    </ng-container>
                    </tbody>
                </table>
            </div>
        </div>
    `,
})
export class AccessibilityComponent implements OnInit, OnDestroy, OnChanges {
    @Input() history: any;
    @Input() domain: any;
    @Input() domainGroup: any;

    historyOutput: any = [];
    totalCount: number = 100;
    allowVerticalAverage: boolean = false;
    crawlAttribute: string = '';
    guidelines: any;

    constructor (
        private pipeService: PipeService,
        private complianceService: ComplianceService,
        private accessibilityGuidelineVersionService: AccessibilityGuidelineVersionService,
        private activeFeatureService: ActiveFeatureService,
        private exporterService: ExporterService,
        private historyPrepareService: HistoryPrepareService,
        private monUtilsService: MonUtilsService,
        private changesHistoryHelperService: ChangesHistoryHelperService,
    ) {}

    ngOnInit (): void {
        this.exporterService.connectToCSVExporter('accessibility', () => {
            return this.historyOutput;
        });
    }

    ngOnDestroy (): void {
        this.exporterService.disconnectFromCSVExporter('accessibility');
    }

    ngOnChanges (changes: any): void {
        this.crawlAttribute = 'accessiblity_issues_count';
        this.guidelines = guidelinesData;

        if (changes.history) {
            this.prepareHistoryOutput(this.history).then((result: any) => {
                this.historyOutput = safeClone(result);
                this.historyOutput.sort((a: any, b: any) => {
                    return b.id - a.id;
                });
            }, () => {});
        }
        this.allowVerticalAverage = this.activeFeatureService.isFeatureActive('monsido_score');
        this.historyOutput = safeClone(this.historyPrepareService.prepareAccessibilityHistory(this.historyOutput));
        this.historyOutput.sort((a: any, b: any) => {
            return b.id - a.id;
        });
    }

    trackByIndex (index: number, item: any): number {
        return index;
    }

    private prepareHistoryOutput (history: any): Promise<any> {
        return this.accessibilityGuidelineVersionService.getVersions(history).then((versionChanges: any) => {
            const result: any[] = [];
            const len = history.length;
            let story: any;
            let entry: any;
            for (let i = 0; i < len; i += 1) {
                story = history[i];
                entry = {
                    id: story.id,
                    post_processing_done_at: story.post_processing_done_at,
                    date: story.date,
                    page_count: this.pipeService.get('monNumber')(story.page_count),
                    error: this.getError(story),
                    changePercentageValue: this.pipeService.get('monNumber')(
                        this.changesHistoryHelperService.getChangePercentageValue(story.id, this.history, [this.crawlAttribute], 'accessibility') / 100,
                        '0.[00]%',
                    ),
                    hasCompliance: this.hasCompliance(story),
                    changeValueColorClass: this.changesHistoryHelperService.getChangeValueColorClass(story.id, this.history, [this.crawlAttribute], 'accessibility'),
                    changeValueIconClass: this.changesHistoryHelperService.getChangeValueIconClass(story.id, this.history, [this.crawlAttribute], 'accessibility'),
                    accessibility_checks: story.accessibility_checks,
                    accessiblity_issues_count: this.pipeService.get('monNumber')(story.accessiblity_issues_count),
                };
                const totalCount = this.totalCount || 1;
                entry.compliancePercentage = this.monUtilsService.withinHundred(1 - entry.error / totalCount) * 100;

                if (this.allowVerticalAverage && story.vertical_scores && entry.hasCompliance) {
                    const comparisonToAverage = Math.round(entry.compliancePercentage - story.vertical_scores.accessibility) / 100;
                    entry.comparisonToAverage = this.pipeService.get('monNumber')(comparisonToAverage, '0.[00]%');
                    entry.comparisonToAverageStyle = this.monUtilsService.getTextStyleByNumberValue(comparisonToAverage);
                }

                for (let j = 0; j < versionChanges.length; j++) {
                    const versionChange = versionChanges[j];
                    if (story.post_processing_done_at === versionChange.time) {
                        entry.versionChange = versionChange;
                        break;
                    }
                }
                result.push(entry);
            }

            return result;
        }, () => {});
    }

    private hasCompliance (history: any): boolean {
        return this.getError(history) !== null;
    }

    private getError (history: any): any {
        const complianceAr = this.complianceService.accessibilityHistoryCompliance(history);
        return Array.isArray(complianceAr) ? complianceAr[1] : null;
    }
}
